import { CSSProperties, FC, PropsWithChildren, useEffect, useRef, useState } from 'react';

import { styled } from '../theme';

const Wrapper = styled('div')<{
    height: string | number;
    timeout: number;
    style?: CSSProperties;
}>(({ height, timeout, style = {} }) => ({
    height: height === 'auto' ? 'auto' : `${height}px`,
    overflow: 'hidden',
    transition: `height ${timeout}ms ease-in-out`,
    ...style,
}));

const Content = styled('div')({
    display: 'flow-root',
});

type Props = PropsWithChildren & {
    in: boolean;
    timeout?: number;
    unmountOnExit?: boolean;
    style?: CSSProperties;
};

const Collapse: FC<Props> = ({
    in: inProp,
    children,
    timeout = 300,
    unmountOnExit = false,
    style = {},
    ...otherProps
}) => {
    const [height, setHeight] = useState<string | number>(0);
    const [isVisible, setIsVisible] = useState(inProp);
    const contentRef = useRef<HTMLDivElement>(null);

    // Step 1: Control mounting/unmounting
    useEffect(() => {
        if (inProp) {
            setIsVisible(true);
        } else if (!inProp && unmountOnExit) {
            // Wait for animation to finish before unmounting
            const timer = setTimeout(() => setIsVisible(false), timeout);
            return () => clearTimeout(timer);
        }
    }, [inProp, unmountOnExit, timeout]);

    // Step 2: Animate height after content is mounted
    useEffect(() => {
        if (!contentRef.current) {
            return;
        }

        if (isVisible && inProp) {
            setHeight(contentRef.current.scrollHeight);
        } else if (!inProp) {
            setHeight(contentRef.current.scrollHeight);
            requestAnimationFrame(() => setHeight(0));
        }
    }, [isVisible, inProp]);

    const handleTransitionEnd = () => {
        if (inProp) {
            setHeight('auto');
        }
    };

    if (!isVisible && unmountOnExit) return null;

    return (
        <Wrapper
            height={height}
            timeout={timeout}
            style={style}
            onTransitionEnd={handleTransitionEnd}
            {...otherProps}
        >
            <Content ref={contentRef}>{children}</Content>
        </Wrapper>
    );
};

export default Collapse;
